import { ExclamationCircleFilled } from '@ant-design/icons';
import { ConnectError } from '@bufbuild/connect';
import { Form, InputNumber, Modal, notification, Typography } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { DiagnocoinBatch } from 'protocol/api/billing_new/dto_diagnocoin_new_pb';

import { LONG_DATE_FORMAT } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import {
  billingModel,
  i18n as billingI18n,
  OrderReasonFields,
} from '@/entities/billing';

import {
  DIAGNOCOIN_ADJUSTMENT_REASONS,
  DiagnocoinAdjustmentReason,
  diagnocoinAdjustmentModalMessages,
} from '../../config';
import { findDiagnocoinAdjustmentOrder } from '../../lib';

import styles from './DiagnocoinAdjustmentModal.module.scss';

const { Text } = Typography;

type DiagnocoinAdjustmentFormFields = {
  amount: number;
  reason: DiagnocoinAdjustmentReason;
  comment?: string;
};

type DiagnocoinAdjustmentModalProps = {
  accountID: string;
  batch?: DiagnocoinBatch;
  onClose: () => void;
};

export const DiagnocoinAdjustmentModal: FC<DiagnocoinAdjustmentModalProps> = (
  props,
) => {
  const { accountID, batch, onClose } = props;

  const dispatch = useAppDispatch();
  const orders = useAppSelector(billingModel.selectors.selectOrders);
  const { formatMessage, formatDate, formatNumber } = useIntl();
  const [form] = Form.useForm<DiagnocoinAdjustmentFormFields>();

  const [loading, setLoading] = useState(false);
  const [requestID, setRequestID] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const reason = Form.useWatch('reason', form);
  const available = Number(batch?.Available ?? 0);
  const batchID = batch?.ID;
  const replayedOrderID =
    batch && isSubmitted && !loading
      ? findDiagnocoinAdjustmentOrder(orders, requestID)?.ID
      : undefined;

  // One ID per opening: billing replays a retried request instead of
  // removing the coins twice.
  useEffect(() => {
    if (batchID) {
      setRequestID(crypto.randomUUID());
      setIsSubmitted(false);
    }
  }, [batchID]);

  const handleClose = () => {
    form.resetFields();
    setIsSubmitted(false);
    onClose();
  };

  const notifySuccess = () => {
    notification.success({
      message: formatMessage(diagnocoinAdjustmentModalMessages.success),
    });
  };

  useEffect(() => {
    if (!replayedOrderID) {
      return;
    }

    notifySuccess();
    handleClose();
  }, [replayedOrderID]);

  const handleCancel = () => {
    if (loading) {
      return;
    }

    handleClose();
  };

  const handleSubmit = async () => {
    if (loading || !batch) {
      return;
    }

    const values = await form.validateFields().catch(() => undefined);

    if (!values) {
      return;
    }

    setLoading(true);
    setIsSubmitted(true);

    try {
      await dispatch(
        billingModel.thunks.createOrder({
          AccountID: accountID,
          Effect: {
            case: 'DiagnocoinAdjustment',
            value: {
              BatchID: batch.ID,
              Amount: BigInt(values.amount),
              Reason: values.reason,
              Comment: values.comment,
              RequestID: requestID,
            },
          },
        }),
      ).unwrap();

      notifySuccess();
      handleClose();
    } catch (error: unknown) {
      const { message } = (error ?? {}) as ConnectError;

      notification.error({
        message:
          message ||
          formatMessage({
            id: 'global.error.somethingWentWrong',
            defaultMessage: 'Something went wrong',
          }),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span className={styles.title}>
          <ExclamationCircleFilled
            className={styles.titleIcon}
            rev={undefined}
          />
          {formatMessage(diagnocoinAdjustmentModalMessages.title)}
        </span>
      }
      open={Boolean(batch)}
      closable={!loading}
      maskClosable={!loading}
      keyboard={!loading}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText={formatMessage(diagnocoinAdjustmentModalMessages.okText)}
      cancelText={formatMessage(diagnocoinAdjustmentModalMessages.cancelText)}
      cancelButtonProps={{ disabled: loading }}
      okButtonProps={{
        danger: true,
        loading,
        disabled: !reason,
      }}
      centered
      width={600}
    >
      <div className={styles.warning}>
        <Text>{formatMessage(diagnocoinAdjustmentModalMessages.content)}</Text>
        <Text strong>
          {formatMessage(diagnocoinAdjustmentModalMessages.batchSummary, {
            purchasedAt: batch?.PurchasedAt
              ? formatDate(batch.PurchasedAt.toDate(), LONG_DATE_FORMAT)
              : '—',
            available: formatNumber(available),
            total: Number(batch?.Total ?? 0),
          })}
        </Text>
      </div>

      <Form form={form} layout="vertical" disabled={loading}>
        <Form.Item
          name="amount"
          label={formatMessage(diagnocoinAdjustmentModalMessages.amountLabel)}
          rules={[
            {
              required: true,
              type: 'integer',
              min: 1,
              max: available,
              message: formatMessage(
                diagnocoinAdjustmentModalMessages.amountValidation,
                { max: formatNumber(available) },
              ),
            },
          ]}
        >
          {/* No min/max here: InputNumber would silently clamp an out-of-range value. */}
          <InputNumber step={1} style={{ width: '100%' }} />
        </Form.Item>

        <OrderReasonFields
          reasonOptions={DIAGNOCOIN_ADJUSTMENT_REASONS}
          reasonMessages={billingI18n.diagnocoinAdjustmentReasonMessages}
        />
      </Form>
    </Modal>
  );
};
